import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import logo from "../../assets/app-logo.svg";
import {
  useCreateUnverifiedUser,
  useInitiateLogin,
} from "../../tanstack-hooks";
import { ApiError } from "@/generated-api";
import {
  SignInFlowType,
  SUCCESSFUL_LOGIN_TYPE,
  SuccessfulLoginType,
} from "./SignInFlowTypes";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useLocaleTranslation } from "@/hooks/useLocaleTranslation";
import Turnstile from "react-turnstile";
import { Capacitor } from "@capacitor/core";
import CaptchaFrame from "./CaptchaFrame";
import LoginWithGoogle from "./LoginWithGoogle";
import LoginWithApple from "./LoginWithApple";
import StatusGreenTag from "./StatusGreenCard";

const AnimatedLogo = ({ src }: { src: string }) => (
  <img src={src} alt="Pulse Logo" className="w-10 h-10 logo-pulse" />
);

export default function LoginEmailCard({
  onSuccess,
  flowType,
  changeFlowType,
  fingerprint,
}: {
  onSuccess: (email: string) => void;
  flowType: SignInFlowType;
  changeFlowType: (type: SignInFlowType) => void;
  fingerprint: string;
}) {
  const [captcha, setCaptcha] = useState("");
  const [creatingNew, setCreatingNew] = useState(false);
  const t = useLocaleTranslation();

  const emailSchema = z.object({
    email: z.string().email(t.LOGIN_EMAIL_CARD.ERRORS.PLEASE_ENTER_VALID),
  });

  const form = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: "",
    },
  });

  const createUnverifiedUserMutation = useCreateUnverifiedUser();
  const initiateLoginMutation = useInitiateLogin();

  const lastLoginType: SuccessfulLoginType | null =
    Object.values(SuccessfulLoginType).find(
      (v) => v === localStorage.getItem(SUCCESSFUL_LOGIN_TYPE),
    ) ?? null;

  function handleLoginEmailSubmit(values: z.infer<typeof emailSchema>) {
    initiateLoginMutation.mutate(
      { email: values.email.toLowerCase() },
      {
        onSuccess: () => {
          onSuccess(values.email);
          form.clearErrors();
        },
        onError: (error) => {
          const err = error as ApiError;
          if (err.status === 404) {
            changeFlowType(SignInFlowType.SIGN_UP);
            setCreatingNew(true);
          } else {
            form.setError("email", {
              type: "manual",
              message: t.SHARED.ERRORS.TRY_AGAIN,
            });
          }
        },
      },
    );
  }

  function handleCreateUnverifiedUserSubmit(
    values: z.infer<typeof emailSchema>,
  ) {
    if (!captcha) {
      form.setError("email", {
        type: "manual",
        message: t.LOGIN_EMAIL_CARD.LABELS.COMPLETE_CAPTCHA,
      });
      return;
    }

    createUnverifiedUserMutation.mutate(
      { email: values.email.toLowerCase(), captcha_token: captcha },
      {
        onSuccess: () => {
          onSuccess(values.email);
          form.clearErrors();
        },
        onError: (error) => {
          const err = error as ApiError;
          if (err.status === 409) {
            form.setError("email", {
              type: "manual",
              message: t.LOGIN_EMAIL_CARD.ERRORS.USER_ALREADY_EXISTS,
            });
          } else if (err.status === 400) {
            form.setError("email", {
              type: "manual",
              message: t.LOGIN_EMAIL_CARD.ERRORS.INVALID_EMAIL,
            });
          } else {
            form.setError("email", {
              type: "manual",
              message: t.SHARED.ERRORS.TRY_AGAIN,
            });
          }
        },
      },
    );
  }

  const handleSubmit =
    flowType === SignInFlowType.LOGIN
      ? handleLoginEmailSubmit
      : handleCreateUnverifiedUserSubmit;

  return (
    <Card
      className={`dark ${
        flowType == SignInFlowType.LOGIN && "px-2"
      } rounded-3xl`}
    >
      <CardHeader className="space-y-4">
        <div className="flex justify-center">
          <AnimatedLogo src={logo} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3">
          <div className="relative flex flex-col w-full">
            <LoginWithApple fingerprint={fingerprint} />
            {lastLoginType === SuccessfulLoginType.APPLE && (
              <div className="absolute -top-9">
                <StatusGreenTag text={t.LOGIN_EMAIL_CARD.LABELS.LAST_USED} />
              </div>
            )}
          </div>
          <div className="relative flex flex-col w-full">
            <LoginWithGoogle fingerprint={fingerprint} />
            {lastLoginType === SuccessfulLoginType.GOOGLE && (
              <div className="absolute -top-9 -right-0">
                <StatusGreenTag text={t.LOGIN_EMAIL_CARD.LABELS.LAST_USED} />
              </div>
            )}
          </div>
        </div>
        <div className="relative flex items-center mt-4 mb-1">
          <div className="flex-grow border-t border-zinc-700"></div>
          <span className="px-2 text-md text-zinc-500">
            {t.LOGIN_EMAIL_CARD.LABELS.OR}
          </span>
          <div className="flex-grow border-t border-zinc-700"></div>
        </div>
        <Form {...form}>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    {t.LOGIN_EMAIL_CARD.LABELS.EMAIL}
                    {lastLoginType === SuccessfulLoginType.EMAIL && (
                      <span className="ml-2">
                        <StatusGreenTag
                          text={t.LOGIN_EMAIL_CARD.LABELS.LAST_USED}
                        />
                      </span>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="text-base"
                      placeholder="name@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {creatingNew && (
              <div className="text-sm">
                {t.LOGIN_EMAIL_CARD.LABELS.CREATING_NEW_ACCOUNT}
              </div>
            )}
            {flowType === SignInFlowType.SIGN_UP &&
              (Capacitor.isNativePlatform() ? (
                <CaptchaFrame onVerify={(token) => setCaptcha(token)} />
              ) : (
                <Turnstile
                  sitekey="0x4AAAAAABdzPiVAQ94CYUw7"
                  onVerify={(token) => setCaptcha(token)}
                  onExpire={() => setCaptcha("")}
                  theme={"dark"}
                />
              ))}
            <Button
              type="submit"
              className="w-full"
              disabled={
                initiateLoginMutation.isPending ||
                createUnverifiedUserMutation.isPending ||
                (flowType === SignInFlowType.SIGN_UP && !captcha)
              }
            >
              {initiateLoginMutation.isPending ||
              createUnverifiedUserMutation.isPending
                ? t.LOGIN_EMAIL_CARD.LABELS.BUTTON_SENDING
                : flowType === SignInFlowType.LOGIN
                  ? t.LOGIN_EMAIL_CARD.LABELS.SIGN_IN
                  : t.LOGIN_EMAIL_CARD.LABELS.SIGN_UP}
            </Button>
          </form>
        </Form>
      </CardContent>
      <CardFooter className="flex justify-center">
        {flowType === SignInFlowType.LOGIN ? (
          <div className="text-sm">
            {t.LOGIN_EMAIL_CARD.LABELS.NO_ACCOUNT_QUESTION}
            <Link to="/sign-up" className="underline">
              {t.LOGIN_EMAIL_CARD.LABELS.SIGN_UP}
            </Link>
          </div>
        ) : (
          <div className="text-sm">
            {t.LOGIN_EMAIL_CARD.LABELS.HAVE_ACCOUNT_QUESTION}
            <Link
              to="/"
              className="underline"
              onClick={() => {
                setCreatingNew(false);
                changeFlowType(SignInFlowType.LOGIN);
              }}
            >
              {t.LOGIN_EMAIL_CARD.LABELS.SIGN_IN}
            </Link>
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
